import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { Loader2Icon } from "lucide-react";
import { useState } from "react";

export const DialogReferralSendLead = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const toastError = useToastError();
  const api = useApi();
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError(t("referrals.sendLead.dialog.validation.invalidEmail"));
    } else {
      setEmailError("");
    }
  };

  const validatePhoneNumber = (phoneNumber: string): boolean => {
    const cleanPhone = phoneNumber.replace(/[^\d+]/g, "");

    // Check for valid phone number patterns
    // Supports: +33123456789, 0123456789, +1234567890, etc.
    const phoneRegex = /^(\+\d{1,3})?\d{7,15}$/;

    return (
      phoneRegex.test(cleanPhone) &&
      cleanPhone.length >= 8 &&
      cleanPhone.length <= 18
    );
  };

  const handlePhoneChange = (value: string) => {
    setPhone(value);
    if (value && !validatePhoneNumber(value)) {
      setPhoneError(t("referrals.sendLead.dialog.validation.invalidPhone"));
    } else {
      setPhoneError("");
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate email before submission
    if (!validateEmail(email)) {
      setEmailError(t("referrals.sendLead.dialog.validation.invalidEmail"));
      return;
    }
    // Validate phone number before submission
    if (!validatePhoneNumber(phone)) {
      setPhoneError(t("referrals.sendLead.dialog.validation.invalidPhone"));
      return;
    }
    try {
      setIsLoading(true);
      await api.post("/my/user/referrals/send-lead", {
        email,
        phone,
      });
      toast({
        variant: "default",
        title: t("referrals.sendLead.dialog.success.title"),
        description: t("referrals.sendLead.dialog.success.description"),
      });
      setEmail("");
      setPhone("");
      setPhoneError("");
      setEmailError("");
      onClose();
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[500px] w-full">
        <DialogHeader>
          <DialogTitle>{t("referrals.sendLead.dialog.title")}</DialogTitle>
          <DialogDescription>
            {t("referrals.sendLead.dialog.description")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="py-4 space-y-4">
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="lead-email">
                {t("referrals.sendLead.dialog.emailLabel")}
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Input
                type="email"
                id="lead-email"
                placeholder={t("referrals.sendLead.dialog.emailPlaceholder")}
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                required
                className={emailError ? "border-destructive" : ""}
              />
              {emailError && (
                <p className="text-sm text-destructive">{emailError}</p>
              )}
            </div>
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="lead-phone">
                {t("referrals.sendLead.dialog.phoneLabel")}
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Input
                type="tel"
                id="lead-phone"
                placeholder={t("referrals.sendLead.dialog.phonePlaceholder")}
                value={phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                required
                className={phoneError ? "border-destructive" : ""}
              />
              {phoneError && (
                <p className="text-sm text-destructive">{phoneError}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost">
                {t("referrals.sendLead.dialog.buttons.cancel")}
              </Button>
            </DialogClose>
            {isLoading ? (
              <Button disabled>
                <Loader2Icon className="w-4 h-4 animate-spin" />
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={!!phoneError || !!emailError || !phone || !email}
              >
                {t("referrals.sendLead.dialog.buttons.send")}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
